import React from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import { MDBBtn, MDBInput, MDBModal, MDBModalDialog, MDBModalContent, MDBModalHeader, MDBModalTitle, MDBModalBody, MDBModalFooter } from 'mdb-react-ui-kit';
import { baseURL } from '../api/baseURL';

type LinkKey = 'snapwiz_youtube_hindi' | 'snapwiz_youtube_english' | 'snapmaster_youtube_hindi' | 'snapmaster_youtube_english';
const LINK_FIELDS: { key: LinkKey, label: string }[] = [
    { key: 'snapwiz_youtube_hindi', label: 'SnapWiz (Customer app) — Hindi video' },
    { key: 'snapwiz_youtube_english', label: 'SnapWiz (Customer app) — English video' },
    { key: 'snapmaster_youtube_hindi', label: 'SnapMaster (Photographer app) — Hindi video' },
    { key: 'snapmaster_youtube_english', label: 'SnapMaster (Photographer app) — English video' },
];

// Every "long legal/agreement text" card (Snapmaster Agreement, each app's
// Terms & Conditions) shares the exact same shape: a preview box, an "Edit"
// button that opens a big modal with a date-line input + textarea, and a
// "Restore original text" button. One config array drives all three so
// adding a fourth is a one-entry change, not a copy-pasted card.
type LongKey = 'agreement' | 'terms_customer' | 'terms_photographer';
const LONG_FIELD_META: Record<LongKey, {
    apiKey: string, effectiveKey: string, defaultsApiKey: string,
    cardTitle: string, cardDescription: React.ReactNode, modalTitle: string,
    dateLabel: string,
}> = {
    agreement: {
        apiKey: 'snapmaster_agreement', effectiveKey: 'snapmaster_agreement_effective', defaultsApiKey: 'agreement_defaults',
        cardTitle: 'Snapmaster Agreement',
        cardDescription: 'The agreement photographers read in the SnapMaster app under Settings.',
        modalTitle: 'Edit Snapmaster Agreement',
        dateLabel: 'Date line under the title (e.g. Dated: 20.07.2025)',
    },
    terms_customer: {
        apiKey: 'terms_customer', effectiveKey: 'terms_customer_effective', defaultsApiKey: 'terms_customer_defaults',
        cardTitle: 'Terms & Conditions — SnapWiz (Customer app)',
        cardDescription: 'The Terms & Conditions customers read in the SnapWiz app.',
        modalTitle: 'Edit SnapWiz Terms & Conditions',
        dateLabel: 'Effective date line (e.g. Effective Date: 01.09.2024)',
    },
    terms_photographer: {
        apiKey: 'terms_photographer', effectiveKey: 'terms_photographer_effective', defaultsApiKey: 'terms_photographer_defaults',
        cardTitle: 'Terms & Conditions — SnapMaster (Photographer app)',
        cardDescription: 'The Terms & Conditions photographers read in the SnapMaster app.',
        modalTitle: 'Edit SnapMaster Terms & Conditions',
        dateLabel: 'Effective date line (e.g. Effective Date: 01.09.2024)',
    },
};
const LONG_FIELD_ORDER: LongKey[] = ['agreement', 'terms_customer', 'terms_photographer'];

type LongFieldState = { text: string, effective: string, defaults: { text: string, effective: string }, requested: boolean, saved: boolean, err: string };
const emptyLongField = (): LongFieldState => ({ text: '', effective: '', defaults: { text: '', effective: '' }, requested: false, saved: false, err: '' });

type AppUpdateKey = 'customer' | 'photographer';
const APP_UPDATE_LABELS: Record<AppUpdateKey, string> = { customer: 'SnapWiz (Customer app)', photographer: 'SnapMaster (Photographer app)' };

// Client-reported: "we need snapwiz and snapmaster testimonials separately
// ... give star adding option only since we cant simply give 5 star for
// every testimonial" — one card + one add-form per app, each row carrying
// its own 1-5 star rating set at add time.
type TestimonialAppKey = 'customer' | 'photographer';
const TESTIMONIAL_APPS: TestimonialAppKey[] = ['customer', 'photographer'];
const TESTIMONIAL_APP_LABELS: Record<TestimonialAppKey, string> = { customer: 'SnapWiz Testimonials', photographer: 'SnapMaster Testimonials' };
type TestimonialItem = { id: string, name: string, text: string, stars: number };
type TestimonialFormState = { name: string, text: string, stars: number };
const emptyTestimonialForm = (): TestimonialFormState => ({ name: '', text: '', stars: 5 });
// Android's versionCode and iOS's CFBundleVersion are independent numbering
// sequences, so each app gets its own version code + store link per platform.
type AppUpdatePlatformKey = 'android' | 'ios';
const APP_UPDATE_PLATFORMS: AppUpdatePlatformKey[] = ['android', 'ios'];
const APP_UPDATE_PLATFORM_LABELS: Record<AppUpdatePlatformKey, string> = { android: 'Android', ios: 'iOS' };
type AppUpdateRow = { latestVersionCode: string, storeUrl: string };
const emptyAppUpdateRow = (): AppUpdateRow => ({ latestVersionCode: '', storeUrl: '' });
type AppUpdatePlatformState = Record<AppUpdatePlatformKey, AppUpdateRow>;
const emptyAppUpdatePlatformState = (): AppUpdatePlatformState => ({ android: emptyAppUpdateRow(), ios: emptyAppUpdateRow() });

/**
 * Edits the exact tutorial videos shown on each app's Home screen, the
 * SnapMaster guidelines text, the Snapmaster Agreement, and both apps' Terms
 * & Conditions. The link fields and the long-text fields open pre-filled
 * with what's live today; saving replaces them and clearing a link (or
 * restoring + saving blank text) puts the app back on its built-in content.
 */
class HomeContentSettings extends React.Component<{ token?: string }, {
    requested: boolean,
    saved: boolean,
    err: boolean,
    links: Record<LinkKey, string>,
    errField: string,
    guidelinesRequested: boolean,
    guidelinesSaved: boolean,
    guidelinesErr: boolean,
    guidelines: string,
    longFields: Record<LongKey, LongFieldState>,
    editor: null | 'guidelines' | LongKey,
    editorBackup: { text: string, effective: string },
    testimonials: Record<TestimonialAppKey, TestimonialItem[]>,
    testimonialForm: Record<TestimonialAppKey, TestimonialFormState>,
    testimonialRequested: boolean,
    testimonialErr: boolean,
    appUpdate: Record<AppUpdateKey, AppUpdatePlatformState>,
    appUpdateRequested: boolean,
    appUpdateSaved: boolean,
    appUpdateErr: string,
}> {
    state = {
        requested: false, saved: false, err: false, errField: '',
        links: { snapwiz_youtube_hindi: '', snapwiz_youtube_english: '', snapmaster_youtube_hindi: '', snapmaster_youtube_english: '' } as Record<LinkKey, string>,
        guidelinesRequested: false, guidelinesSaved: false, guidelinesErr: false, guidelines: '',
        longFields: {
            agreement: emptyLongField(), terms_customer: emptyLongField(), terms_photographer: emptyLongField(),
        } as Record<LongKey, LongFieldState>,
        editor: null as null | 'guidelines' | LongKey, editorBackup: { text: '', effective: '' },
        testimonials: { customer: [], photographer: [] } as Record<TestimonialAppKey, TestimonialItem[]>,
        testimonialForm: { customer: emptyTestimonialForm(), photographer: emptyTestimonialForm() } as Record<TestimonialAppKey, TestimonialFormState>,
        testimonialRequested: false, testimonialErr: false,
        appUpdate: { customer: emptyAppUpdatePlatformState(), photographer: emptyAppUpdatePlatformState() } as Record<AppUpdateKey, AppUpdatePlatformState>,
        appUpdateRequested: false, appUpdateSaved: false, appUpdateErr: '',
    };

    componentDidMount(): void {
        this.fetchConfig();
    }

    fetchConfig = () => {
        if (!this.props.token) return;
        axios.get(`${baseURL}/home-content-config`, { headers: { Authorization: `Bearer ${this.props.token}` } })
            .then((response: any) => {
                if (response?.data?.message === 'success') {
                    const links = Object.fromEntries(LINK_FIELDS.map(f => [f.key, response.data[f.key] || ''])) as Record<LinkKey, string>;
                    const longFields = Object.fromEntries(LONG_FIELD_ORDER.map(key => {
                        const meta = LONG_FIELD_META[key];
                        return [key, {
                            ...emptyLongField(),
                            text: response.data[meta.apiKey] || '',
                            effective: response.data[meta.effectiveKey] || '',
                            defaults: response.data[meta.defaultsApiKey] || { text: '', effective: '' },
                        }];
                    })) as Record<LongKey, LongFieldState>;
                    const appUpdate = Object.fromEntries((['customer', 'photographer'] as AppUpdateKey[]).map(k => [k, Object.fromEntries(APP_UPDATE_PLATFORMS.map(p => [p, {
                        latestVersionCode: response.data.app_update?.[k]?.[p]?.latest_version_code ? String(response.data.app_update[k][p].latest_version_code) : '',
                        storeUrl: response.data.app_update?.[k]?.[p]?.store_url || '',
                    }]))])) as Record<AppUpdateKey, AppUpdatePlatformState>;
                    this.setState({
                        links, guidelines: response.data.snapmaster_guidelines || '', longFields,
                        testimonials: {
                            customer: response.data.testimonials_customer || [],
                            photographer: response.data.testimonials_photographer || [],
                        },
                        appUpdate,
                    });
                }
            })
            .catch(() => {});
    }

    save = () => {
        if (this.state.requested || !this.props.token) return;
        this.setState({ requested: true, saved: false, err: false, errField: '' });
        axios.post(`${baseURL}/update-home-content-config`, {
            ...this.state.links,
        }, { headers: { Authorization: `Bearer ${this.props.token}` } })
            .then((response: any) => {
                if (response?.data?.message === 'success') {
                    this.setState({ requested: false, saved: true });
                } else {
                    this.setState({ requested: false, err: true });
                }
            })
            .catch((error: any) => {
                const field = error?.response?.data?.message === 'invalid_url' ? error.response.data.field : '';
                this.setState({ requested: false, err: true, errField: field });
            });
    }

    saveGuidelines = (onDone?: () => void) => {
        if (this.state.guidelinesRequested || !this.props.token) return;
        this.setState({ guidelinesRequested: true, guidelinesSaved: false, guidelinesErr: false });
        axios.post(`${baseURL}/update-home-content-config`, {
            snapmaster_guidelines: this.state.guidelines,
        }, { headers: { Authorization: `Bearer ${this.props.token}` } })
            .then((response: any) => {
                if (response?.data?.message === 'success') {
                    this.setState({ guidelinesRequested: false, guidelinesSaved: true });
                    onDone?.();
                } else {
                    this.setState({ guidelinesRequested: false, guidelinesErr: true });
                }
            })
            .catch(() => this.setState({ guidelinesRequested: false, guidelinesErr: true }));
    }

    saveLongField = (key: LongKey, onDone?: () => void) => {
        const field = this.state.longFields[key];
        if (field.requested || !this.props.token) return;
        const meta = LONG_FIELD_META[key];
        this.setState({ longFields: { ...this.state.longFields, [key]: { ...field, requested: true, saved: false, err: '' } } });
        axios.post(`${baseURL}/update-home-content-config`, {
            [meta.apiKey]: field.text,
            [meta.effectiveKey]: field.effective,
        }, { headers: { Authorization: `Bearer ${this.props.token}` } })
            .then((response: any) => {
                const cur = this.state.longFields[key];
                if (response?.data?.message === 'success') {
                    this.setState({ longFields: { ...this.state.longFields, [key]: { ...cur, requested: false, saved: true } } });
                    onDone?.();
                } else {
                    this.setState({ longFields: { ...this.state.longFields, [key]: { ...cur, requested: false, err: 'Could not save — try again.' } } });
                }
            })
            .catch((error: any) => {
                const cur = this.state.longFields[key];
                const err = error?.response?.data?.message === 'text_too_long' ? 'This text is too long to save.' : 'Could not save — try again.';
                this.setState({ longFields: { ...this.state.longFields, [key]: { ...cur, requested: false, err } } });
            });
    }

    addTestimonial = (app: TestimonialAppKey) => {
        const form = this.state.testimonialForm[app];
        const name = form.name.trim();
        const text = form.text.trim();
        if (!name || !text || this.state.testimonialRequested || !this.props.token) return;
        this.setState({ testimonialRequested: true, testimonialErr: false });
        axios.post(`${baseURL}/add-testimonial`, { app, name, text, stars: form.stars }, { headers: { Authorization: `Bearer ${this.props.token}` } })
            .then((response: any) => {
                if (response?.data?.message === 'success') {
                    this.setState({
                        testimonialRequested: false,
                        testimonialForm: { ...this.state.testimonialForm, [app]: emptyTestimonialForm() },
                    });
                    this.fetchConfig();
                } else {
                    this.setState({ testimonialRequested: false, testimonialErr: true });
                }
            })
            .catch(() => this.setState({ testimonialRequested: false, testimonialErr: true }));
    }

    removeTestimonial = (app: TestimonialAppKey, testimonialId: string) => {
        if (this.state.testimonialRequested || !this.props.token) return;
        if (!window.confirm('Remove this testimonial?')) return;
        this.setState({ testimonialRequested: true, testimonialErr: false });
        axios.post(`${baseURL}/remove-testimonial`, { app, testimonialId }, { headers: { Authorization: `Bearer ${this.props.token}` } })
            .then((response: any) => {
                if (response?.data?.message === 'success') {
                    this.setState({ testimonialRequested: false });
                    this.fetchConfig();
                } else {
                    this.setState({ testimonialRequested: false, testimonialErr: true });
                }
            })
            .catch(() => this.setState({ testimonialRequested: false, testimonialErr: true }));
    }

    saveAppUpdate = () => {
        if (this.state.appUpdateRequested || !this.props.token) return;
        this.setState({ appUpdateRequested: true, appUpdateSaved: false, appUpdateErr: '' });
        const app_update = Object.fromEntries((['customer', 'photographer'] as AppUpdateKey[]).map(k => [k, Object.fromEntries(APP_UPDATE_PLATFORMS.map(p => [p, {
            latest_version_code: this.state.appUpdate[k][p].latestVersionCode.trim() === '' ? 0 : Number(this.state.appUpdate[k][p].latestVersionCode),
            store_url: this.state.appUpdate[k][p].storeUrl.trim(),
        }]))]));
        axios.post(`${baseURL}/update-home-content-config`, { app_update }, { headers: { Authorization: `Bearer ${this.props.token}` } })
            .then((response: any) => {
                if (response?.data?.message === 'success') {
                    this.setState({ appUpdateRequested: false, appUpdateSaved: true });
                } else {
                    this.setState({ appUpdateRequested: false, appUpdateErr: 'Could not save — try again.' });
                }
            })
            .catch((error: any) => {
                const msg = error?.response?.data?.message;
                this.setState({
                    appUpdateRequested: false,
                    appUpdateErr: msg === 'invalid_version_code' ? 'Version code must be a whole number.' : msg === 'invalid_url' ? 'Store link must start with http:// or https://' : 'Could not save — try again.',
                });
            });
    }

    openEditor = (which: 'guidelines' | LongKey) => {
        this.setState({
            editor: which,
            editorBackup: which === 'guidelines'
                ? { text: this.state.guidelines, effective: '' }
                : { text: this.state.longFields[which].text, effective: this.state.longFields[which].effective },
            guidelinesErr: false,
        });
        if (which !== 'guidelines') {
            const cur = this.state.longFields[which];
            this.setState({ longFields: { ...this.state.longFields, [which]: { ...cur, err: '' } } });
        }
    }

    // Closing without saving puts back whatever the text was when the editor opened.
    cancelEditor = () => {
        const { editor, editorBackup } = this.state;
        if (editor === 'guidelines') this.setState({ guidelines: editorBackup.text });
        else if (editor) {
            const cur = this.state.longFields[editor];
            this.setState({ longFields: { ...this.state.longFields, [editor]: { ...cur, text: editorBackup.text, effective: editorBackup.effective } } });
        }
        this.setState({ editor: null });
    }

    restoreLongFieldDefault = (key: LongKey) => {
        if (!window.confirm('Replace the editor text with the original built-in text? Nothing changes in the app until you press Save.')) return;
        const cur = this.state.longFields[key];
        this.setState({ longFields: { ...this.state.longFields, [key]: { ...cur, text: cur.defaults.text, effective: cur.defaults.effective, saved: false } } });
    }

    /** Read-only preview of a text field; clicking it (or Edit) opens the big editor. */
    renderPreview = (value: string, placeholder: string, onOpen: () => void) => (
        <div
            onClick={onOpen}
            title="Click to edit"
            style={{
                position: 'relative', cursor: 'pointer', maxHeight: 190, overflow: 'hidden', whiteSpace: 'pre-wrap',
                background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8,
                padding: '12px 16px', fontSize: 14, lineHeight: 1.6, marginBottom: 12,
                color: value ? 'inherit' : 'rgba(255,255,255,0.4)',
            }}
        >
            {value || placeholder}
            {value ? <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, height: 44, background: 'linear-gradient(to bottom, rgba(27,30,39,0), rgba(27,30,39,0.95))', pointerEvents: 'none' }} /> : null}
        </div>
    )

    renderLongFieldCard = (key: LongKey) => {
        const meta = LONG_FIELD_META[key];
        const field = this.state.longFields[key];
        return (
            <div className="card" style={{ marginBottom: 16 }} key={key}>
                <div className="card-body">
                    <h4 className="card-title">{meta.cardTitle}</h4>
                    <p style={{ fontSize: 13, opacity: 0.7, marginBottom: 8 }}>
                        {meta.cardDescription} It opens with the text that is live today — edit it and press Save to
                        replace it. Separate each section with a blank line. Start a line with <code>#</code> for a
                        heading or <code>##</code> for a sub-heading; everything else is normal text. Changes show in
                        the app after the next app open.
                    </p>
                    <div style={{ fontSize: 12.5, opacity: 0.7, marginBottom: 8 }}>Date line: <b>{field.effective || '—'}</b></div>
                    {this.renderPreview(field.text, 'Loading…', () => this.openEditor(key))}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <MDBBtn onClick={() => this.openEditor(key)}>Edit</MDBBtn>
                        {field.saved ? <span style={{ color: '#3ddc84', fontSize: 13 }}>Saved</span> : null}
                    </div>
                </div>
            </div>
        );
    }

    render(): React.ReactNode {
        const editorField = this.state.editor && this.state.editor !== 'guidelines' ? this.state.longFields[this.state.editor] : null;
        const editorMeta = this.state.editor && this.state.editor !== 'guidelines' ? LONG_FIELD_META[this.state.editor] : null;
        return (
            <>
            <div className="card" style={{ marginBottom: 16 }}>
                <div className="card-body">
                    <h4 className="card-title">Home Screen Tutorial Videos</h4>
                    <p style={{ fontSize: 13, opacity: 0.7, marginBottom: 16 }}>
                        These are the exact "Watch the guide" videos shown on each app's Home screen (one Hindi and
                        one English per app). The boxes show the links that are live now — paste a new YouTube link
                        to replace one. Clear a box to go back to the video built into the app.
                    </p>
                    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 12 }}>
                        {LINK_FIELDS.map(f => (
                            <div key={f.key} style={{ flex: '1 1 320px' }}>
                                <MDBInput
                                    contrast
                                    label={f.label}
                                    type="text"
                                    value={this.state.links[f.key]}
                                    onChange={(e) => this.setState({ links: { ...this.state.links, [f.key]: e.target.value }, saved: false, err: false })}
                                />
                            </div>
                        ))}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <MDBBtn onClick={this.save} disabled={this.state.requested}>
                            {this.state.requested ? 'Saving…' : 'Save'}
                        </MDBBtn>
                        {this.state.saved ? <span style={{ color: '#3ddc84', fontSize: 13 }}>Saved</span> : null}
                        {this.state.err ? (
                            <span style={{ color: '#ff6b6b', fontSize: 13 }}>
                                {this.state.errField
                                    ? `"${LINK_FIELDS.find(f => f.key === this.state.errField)?.label || 'A link'}" must start with http:// or https://`
                                    : 'Could not save — try again.'}
                            </span>
                        ) : null}
                    </div>
                </div>
            </div>

            <div className="card" style={{ marginBottom: 16 }}>
                <div className="card-body">
                    <h4 className="card-title">App Update</h4>
                    <p style={{ fontSize: 13, opacity: 0.7, marginBottom: 16 }}>
                        Both apps compare their own build number against the version code you set here, on every
                        app open (SnapWiz also re-checks every time the customer returns to Home). <b>SnapWiz</b>{' '}
                        shows a dismissible "update available" button that reappears until they update.{' '}
                        <b>SnapMaster</b> blocks the app entirely until the photographer updates. Android and iOS
                        builds are numbered independently, so each platform has its own version code and store
                        link below — set the version code to the build number of the release you just published
                        on that store (0 or blank turns the check off for that app+platform). Requires app builds
                        with this feature installed — older installs won't check.
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, marginBottom: 12 }}>
                        {(['customer', 'photographer'] as AppUpdateKey[]).map(k => (
                            <div key={k}>
                                <div style={{ fontSize: 12.5, fontWeight: 700, opacity: 0.8, marginBottom: 8 }}>{APP_UPDATE_LABELS[k]}</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                                    {APP_UPDATE_PLATFORMS.map(p => (
                                        <div key={p} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                                            <div style={{ width: 64, fontSize: 12.5, opacity: 0.6, paddingTop: 14 }}>{APP_UPDATE_PLATFORM_LABELS[p]}</div>
                                            <div style={{ width: 140 }}>
                                                <MDBInput
                                                    contrast
                                                    label="Version code"
                                                    type="number"
                                                    value={this.state.appUpdate[k][p].latestVersionCode}
                                                    onChange={(e) => this.setState({ appUpdate: { ...this.state.appUpdate, [k]: { ...this.state.appUpdate[k], [p]: { ...this.state.appUpdate[k][p], latestVersionCode: e.target.value } } }, appUpdateSaved: false })}
                                                />
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                <MDBInput
                                                    contrast
                                                    label="Store link"
                                                    type="text"
                                                    value={this.state.appUpdate[k][p].storeUrl}
                                                    onChange={(e) => this.setState({ appUpdate: { ...this.state.appUpdate, [k]: { ...this.state.appUpdate[k], [p]: { ...this.state.appUpdate[k][p], storeUrl: e.target.value } } }, appUpdateSaved: false })}
                                                />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <MDBBtn onClick={this.saveAppUpdate} disabled={this.state.appUpdateRequested}>
                            {this.state.appUpdateRequested ? 'Saving…' : 'Save'}
                        </MDBBtn>
                        {this.state.appUpdateSaved ? <span style={{ color: '#3ddc84', fontSize: 13 }}>Saved</span> : null}
                        {this.state.appUpdateErr ? <span style={{ color: '#ff6b6b', fontSize: 13 }}>{this.state.appUpdateErr}</span> : null}
                    </div>
                </div>
            </div>

            <div className="card" style={{ marginBottom: 16 }}>
                <div className="card-body">
                    <h4 className="card-title">SnapMaster Guidelines</h4>
                    <p style={{ fontSize: 13, opacity: 0.7, marginBottom: 16 }}>
                        Shown on a new "Guidelines" screen in the Photographer app's Settings. Plain text —
                        one guideline per line works well. Leave blank to hide the screen entirely.
                    </p>
                    {this.renderPreview(this.state.guidelines, 'No guidelines added yet — click to add.', () => this.openEditor('guidelines'))}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <MDBBtn onClick={() => this.openEditor('guidelines')}>Edit guidelines</MDBBtn>
                        {this.state.guidelinesSaved ? <span style={{ color: '#3ddc84', fontSize: 13 }}>Saved</span> : null}
                    </div>
                </div>
            </div>

            {TESTIMONIAL_APPS.map(app => (
            <div className="card" style={{ marginBottom: 16 }} key={app}>
                <div className="card-body">
                    <h4 className="card-title">{TESTIMONIAL_APP_LABELS[app]}</h4>
                    <p style={{ fontSize: 13, opacity: 0.7, marginBottom: 16 }}>
                        {app === 'customer'
                            ? 'Shown on the SnapWiz Home screen — falls back to a few built-in ones until you add real testimonials here.'
                            : 'Shown on the SnapMaster Home screen — reads these live, no built-in fallback.'}
                    </p>
                    <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 12, alignItems: 'flex-end' }}>
                        <div style={{ flex: '1 1 200px' }}>
                            <MDBInput
                                contrast
                                label="Name"
                                type="text"
                                value={this.state.testimonialForm[app].name}
                                onChange={(e) => this.setState({ testimonialForm: { ...this.state.testimonialForm, [app]: { ...this.state.testimonialForm[app], name: e.target.value } } })}
                            />
                        </div>
                        <div style={{ flex: '2 1 320px' }}>
                            <MDBInput
                                contrast
                                label="Testimonial text"
                                type="text"
                                value={this.state.testimonialForm[app].text}
                                onChange={(e) => this.setState({ testimonialForm: { ...this.state.testimonialForm, [app]: { ...this.state.testimonialForm[app], text: e.target.value } } })}
                            />
                        </div>
                        <div>
                            <div style={{ fontSize: 11, opacity: 0.6, marginBottom: 4 }}>Rating</div>
                            <div style={{ display: 'flex', gap: 2 }}>
                                {[1, 2, 3, 4, 5].map(n => (
                                    <span
                                        key={n}
                                        onClick={() => this.setState({ testimonialForm: { ...this.state.testimonialForm, [app]: { ...this.state.testimonialForm[app], stars: n } } })}
                                        style={{ cursor: 'pointer', fontSize: 22, lineHeight: 1, color: n <= this.state.testimonialForm[app].stars ? '#F59E0B' : 'rgba(255,255,255,0.25)' }}
                                    >
                                        ★
                                    </span>
                                ))}
                            </div>
                        </div>
                        <MDBBtn
                            onClick={() => this.addTestimonial(app)}
                            disabled={this.state.testimonialRequested || !this.state.testimonialForm[app].name.trim() || !this.state.testimonialForm[app].text.trim()}
                        >
                            Add
                        </MDBBtn>
                    </div>
                    {this.state.testimonialErr ? <p style={{ color: '#ff6b6b', fontSize: 13, marginBottom: 12 }}>Could not save — try again.</p> : null}
                    {this.state.testimonials[app].length === 0 ? (
                        <p style={{ fontSize: 13, opacity: 0.6 }}>No testimonials added yet.</p>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {this.state.testimonials[app].map(t => (
                                <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 2px', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ fontSize: 13, fontWeight: 700 }}>{t.name}</div>
                                        <div style={{ fontSize: 12, color: '#F59E0B', marginTop: 2 }}>{'★'.repeat(t.stars || 5)}<span style={{ color: 'rgba(255,255,255,0.25)' }}>{'★'.repeat(5 - (t.stars || 5))}</span></div>
                                        <div style={{ fontSize: 12.5, opacity: 0.75, marginTop: 2 }}>{t.text}</div>
                                    </div>
                                    <MDBBtn color="secondary" size="sm" disabled={this.state.testimonialRequested} onClick={() => this.removeTestimonial(app, t.id)}>
                                        Remove
                                    </MDBBtn>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
            ))}

            {LONG_FIELD_ORDER.map(key => this.renderLongFieldCard(key))}

            <MDBModal open={!!this.state.editor} onClose={this.cancelEditor} tabIndex='-1' style={{ display: 'flex' }}>
                <MDBModalDialog centered size='xl' style={{ maxWidth: 1100, width: '92vw' }}>
                    <MDBModalContent style={{ borderRadius: 14, overflow: 'hidden' }}>
                        <MDBModalHeader style={{ padding: '18px 24px', alignItems: 'center' }}>
                            <MDBModalTitle style={{ fontSize: '1.15rem', fontWeight: 600, margin: 0 }}>
                                {editorMeta ? editorMeta.modalTitle : 'Edit SnapMaster Guidelines'}
                            </MDBModalTitle>
                        </MDBModalHeader>
                        <MDBModalBody style={{ padding: '20px 24px' }}>
                            {editorMeta && editorField ? (
                                <>
                                    <p style={{ fontSize: 12.5, opacity: 0.7, marginBottom: 12 }}>
                                        Separate each section with a blank line. Start a line with <code>#</code> for a heading or
                                        <code> ##</code> for a sub-heading; everything else is normal text.
                                    </p>
                                    <div style={{ maxWidth: 420, marginBottom: 14 }}>
                                        <MDBInput
                                            contrast
                                            label={editorMeta.dateLabel}
                                            type="text"
                                            value={editorField.effective}
                                            onChange={(e) => this.setState({ longFields: { ...this.state.longFields, [this.state.editor as LongKey]: { ...editorField, effective: e.target.value, saved: false } } })}
                                        />
                                    </div>
                                    <textarea
                                        className="form-control"
                                        autoFocus
                                        style={{ height: '58vh', fontSize: 15, lineHeight: 1.65, resize: 'none', background: 'rgba(255,255,255,0.04)', color: 'inherit', fontFamily: 'inherit', padding: '14px 16px' }}
                                        value={editorField.text}
                                        onChange={(e) => this.setState({ longFields: { ...this.state.longFields, [this.state.editor as LongKey]: { ...editorField, text: e.target.value, saved: false, err: '' } } })}
                                    />
                                </>
                            ) : (
                                <>
                                    <p style={{ fontSize: 12.5, opacity: 0.7, marginBottom: 12 }}>
                                        Shown on the "Guidelines" screen in the SnapMaster app's Settings. One guideline per line works well. Leave blank to hide the screen.
                                    </p>
                                    <textarea
                                        className="form-control"
                                        autoFocus
                                        style={{ height: '62vh', fontSize: 15, lineHeight: 1.65, resize: 'none', background: 'rgba(255,255,255,0.04)', color: 'inherit', fontFamily: 'inherit', padding: '14px 16px' }}
                                        placeholder={"e.g.\nArrive at the venue at least 15 minutes before the shoot start time.\nAlways complete the start-OTP with the customer before beginning.\nDeliver edited photos/videos within the agreed timeline."}
                                        value={this.state.guidelines}
                                        onChange={(e) => this.setState({ guidelines: e.target.value, guidelinesSaved: false, guidelinesErr: false })}
                                    />
                                </>
                            )}
                        </MDBModalBody>
                        <MDBModalFooter style={{ padding: '14px 24px', gap: 12, justifyContent: 'space-between' }}>
                            <div>
                                {editorMeta && editorField ? (
                                    <MDBBtn
                                        color="secondary"
                                        disabled={editorField.requested || !editorField.defaults.text}
                                        onClick={() => this.restoreLongFieldDefault(this.state.editor as LongKey)}
                                    >
                                        Restore original text
                                    </MDBBtn>
                                ) : null}
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                {editorField?.err ? <span style={{ color: '#ff6b6b', fontSize: 13 }}>{editorField.err}</span> : null}
                                {this.state.editor === 'guidelines' && this.state.guidelinesErr ? <span style={{ color: '#ff6b6b', fontSize: 13 }}>Could not save — try again.</span> : null}
                                <MDBBtn color="secondary" onClick={this.cancelEditor}>Cancel</MDBBtn>
                                {editorMeta && editorField ? (
                                    <MDBBtn disabled={editorField.requested || !editorField.text.trim()} onClick={() => this.saveLongField(this.state.editor as LongKey, () => this.setState({ editor: null }))}>
                                        {editorField.requested ? 'Saving…' : 'Save'}
                                    </MDBBtn>
                                ) : (
                                    <MDBBtn disabled={this.state.guidelinesRequested} onClick={() => this.saveGuidelines(() => this.setState({ editor: null }))}>
                                        {this.state.guidelinesRequested ? 'Saving…' : 'Save'}
                                    </MDBBtn>
                                )}
                            </div>
                        </MDBModalFooter>
                    </MDBModalContent>
                </MDBModalDialog>
            </MDBModal>
            </>
        );
    }
}

const mapStateToProps = (state: any) => ({
    token: state.auth?.token,
});

export default connect(mapStateToProps)(HomeContentSettings);
